import React, { useState } from 'react';
import { Car, FileDown, Loader2, PencilLine, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { C, Panel, StatusBadge, TableShell } from '@/design-system/dna';
import { brl, PAY_STATUS } from '../shared';
import { ProdutosEditor } from './ProdutosEditor';
import { ExtrasDialog } from './ExtrasDialog';

function PdfButton({ eventId, kind, label, icon: I }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/eventos/${eventId}/lista-${kind}.pdf`, { responseType: 'blob' }); window.open(URL.createObjectURL(new Blob([data], { type: 'application/pdf' })), '_blank'); }
    catch { toast.error('Falha ao gerar a lista.'); } finally { setBusy(false); }
  };
  return <Button size="sm" variant="outline" onClick={go} disabled={busy} data-testid={`evento-lista-${kind}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <I className="h-4 w-4" />} {label} <FileDown className="h-3.5 w-3.5" /></Button>;
}

const outros = (p) => p.extras_lines.filter((x) => !['convite', 'estacionamento'].includes(x.tipo)).map((x) => `${x.qty}× ${x.nome}`).join(', ');

function Controle({ parts, onEdit }) {
  const head = ['Aluno', 'Evento', 'Extras', 'Convites', 'Estac.', 'Total', 'Pago', 'Saldo', 'Status', ''];
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[920px] text-[12.5px]" data-testid="evento-extras-controle">
        <thead><tr className="text-left text-[10.5px] font-bold uppercase tracking-wider" style={{ color: C.muted }}>{head.map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
        <tbody className="divide-y" style={{ borderColor: C.line }}>
          {parts.map((p) => (
            <tr key={p.student_id} className="hover:bg-[#FBF9FD]" data-testid={`evento-extras-row-${p.student_id}`}>
              <td className="px-3 py-2"><b style={{ color: C.ink }}>{p.student_name}</b><span className="block text-[11px]" style={{ color: C.muted }}>{p.class_name}{outros(p) ? ` · ${outros(p)}` : ''}</span></td>
              <td className="px-3 py-2 tabular-nums">{brl(p.valor_evento)}</td>
              <td className="px-3 py-2 tabular-nums">{brl(p.valor_extras)}</td>
              <td className="px-3 py-2 text-center tabular-nums">{p.qtd_convites || '—'}</td>
              <td className="px-3 py-2 text-center tabular-nums">{p.qtd_estacionamento || '—'}</td>
              <td className="px-3 py-2 font-bold tabular-nums">{brl(p.valor_cobrado)}</td>
              <td className="px-3 py-2 tabular-nums" style={{ color: C.green }}>{brl(p.pago)}</td>
              <td className="px-3 py-2 tabular-nums">{brl(p.saldo)}</td>
              <td className="px-3 py-2"><StatusBadge tone={PAY_STATUS[p.status]?.tone}>{PAY_STATUS[p.status]?.label || p.status}</StatusBadge></td>
              <td className="px-3 py-2 text-right">{p.participa && <Button size="sm" variant="ghost" onClick={() => onEdit(p)} data-testid={`evento-extras-editar-${p.student_id}`}><PencilLine className="h-4 w-4" /> Extras</Button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Listas({ parts }) {
  const guests = parts.flatMap((p) => p.extras_lines.filter((x) => x.tipo === 'convite').flatMap((x) => Array.from({ length: x.qty }, (_, i) => ({ nome: x.convidados[i] || '(nome não informado)', p }))));
  const cars = parts.filter((p) => p.qtd_estacionamento);
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel title={`Convidados (${guests.length})`} kicker="Convites avulsos" testid="evento-lista-convidados-painel">
        {guests.length ? <ol className="space-y-1 text-[12.5px]">{guests.map((g, i) => <li key={i} className="flex justify-between gap-2"><span><b>{i + 1}.</b> {g.nome}</span><span style={{ color: C.muted }}>{g.p.student_name}</span></li>)}</ol> : <p className="text-[12.5px]" style={{ color: C.muted }}>Nenhum convite avulso.</p>}
      </Panel>
      <Panel title={`Estacionamento (${cars.reduce((s, p) => s + p.qtd_estacionamento, 0)} vaga(s))`} kicker="Responsável · aluno · placa" testid="evento-lista-estacionamento-painel">
        {cars.length ? <ul className="space-y-1 text-[12.5px]">{cars.map((p) => <li key={p.student_id} className="flex flex-wrap justify-between gap-2"><span><b>{p.responsible_name || '—'}</b> · {p.student_name} · {p.qtd_estacionamento}×</span><span style={{ color: C.muted }}>{p.extras_lines.filter((x) => x.tipo === 'estacionamento').flatMap((x) => x.veiculos).join(', ') || 'sem placa'} · {PAY_STATUS[p.status]?.label}</span></li>)}</ul> : <p className="text-[12.5px]" style={{ color: C.muted }}>Nenhuma vaga vendida.</p>}
      </Panel>
    </div>
  );
}

function Resultado({ produtos }) {
  const cols = [['Produto', (p) => <b style={{ color: C.ink }}>{p.nome}</b>], ['Venda', (p) => brl(p.valor)], ['Custo unit.', (p) => brl(p.custo_unitario)],
    ['Qtd. confirmada', (p) => p.qtd_confirmada], ['Qtd. paga', (p) => p.qtd_paga], ['Receita bruta', (p) => brl(p.receita_bruta)],
    ['Custo total', (p) => brl(p.custo_total)], ['Taxas', (p) => brl(p.taxas)], ['Receita líquida', (p) => brl(p.receita_liquida)],
    ['Lucro', (p) => <b style={{ color: p.lucro >= 0 ? C.green : '#D6336C' }} data-testid={`evento-resultado-lucro-${p.id}`}>{brl(p.lucro)}</b>]];
  const tot = (k) => produtos.reduce((s, p) => s + (p[k] || 0), 0);
  return (
    <TableShell title="Resultado por produto extra" testid="evento-extras-resultado"
      footer="Receita = recebido rateado pela composição do título (qtd. paga × venda quando quitado) · Custo = qtd. confirmada × custo unitário · Lucro = receita − taxas − custo. Recalculado automaticamente.">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[980px] text-[12.5px]">
          <thead><tr className="text-left text-[10.5px] font-bold uppercase tracking-wider" style={{ color: C.muted }}>{cols.map(([h]) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
          <tbody className="divide-y" style={{ borderColor: C.line }}>
            {produtos.map((p) => <tr key={p.id} data-testid={`evento-resultado-${p.id}`}>{cols.map(([h, f]) => <td key={h} className="px-3 py-2 tabular-nums">{f(p)}</td>)}</tr>)}
            {produtos.length > 1 && <tr className="font-bold" style={{ background: C.paper }}><td className="px-3 py-2">Total</td><td /><td /><td className="px-3 py-2">{tot('qtd_confirmada')}</td><td className="px-3 py-2">{tot('qtd_paga')}</td>
              {['receita_bruta', 'custo_total', 'taxas', 'receita_liquida', 'lucro'].map((k) => <td key={k} className="px-3 py-2 tabular-nums">{brl(tot(k))}</td>)}</tr>}
          </tbody>
        </table>
      </div>
    </TableShell>
  );
}

/** Aba Extras: produtos do evento, controle por aluno/família e listas finais. */
export function Extras({ data, setData, eventId }) {
  const [edit, setEdit] = useState(null);
  const parts = data.participants.filter((p) => p.participa);
  return (
    <div className="space-y-4" data-testid="evento-extras">
      <ProdutosEditor data={data} setData={setData} eventId={eventId} />
      {data.produtos.length > 0 && <Resultado produtos={data.produtos} />}
      <TableShell title="Controle por aluno / família" testid="evento-extras-tabela"
        toolbar={<><PdfButton eventId={eventId} kind="convidados" label="Lista de convidados" icon={Ticket} /><PdfButton eventId={eventId} kind="estacionamento" label="Lista de estacionamento" icon={Car} /></>}
        footer="Pagamentos (PIX, dinheiro, débito, crédito, PIX no cartão, parcial) na aba Recebimentos — o saldo já inclui os extras.">
        <Controle parts={parts} onEdit={setEdit} />
      </TableShell>
      <Listas parts={parts} />
      <ExtrasDialog part={edit} data={data} eventId={eventId} setData={setData} onClose={() => setEdit(null)} />
    </div>
  );
}
